import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { Columns } from "./data";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import APISearchInput from "../../../../../components/search/APISearchInput";
import CustomStyledTable from "../../../../../components/table/CustomStyledTable";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import ErpIntegrationMappingsModalForm from "./modal";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { PaymentInstitutionBranchOrderBy } from "../../../../../models/payment-institution-branch.dto";
import {
	useGetErpIntegrationMappingSearch,
	useGetErpIntegrationMapping,
	useDeleteErpIntegrationMapping,
} from "../../../../../services/erp-integration-mapping.service";
import { useGetPaymentInstitutionBranch } from "../../../../../services/payment-institution-branch.service";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../../helpers/usePermissionManager";
import { mappingColumns, mappingColumnVisibility } from "./tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../../models/table-configuration.dto";
import useUserStore from "../../../../../state-management/useUserStore";

const ErpIntegrationMappings = () => {
	const { currentCompany } = useCompanyStore();
	const { currentUser, userType } = useUserStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);

	const { mutate: searchErpIntegrationMappings } =
		useGetErpIntegrationMappingSearch();
	const {
		tableAPIRef,
		tableData,
		fetchedTableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams({
		fetchHook: useGetErpIntegrationMapping,
		options: {
			companyId: currentCompany?.id,
			orderBy: undefined,
		},
	});

	// Fetched data for select fields requiring data from database
	const { data: paymentInstitutionBranches } = useGetPaymentInstitutionBranch({
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: PaymentInstitutionBranchOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteErpIntegrationMappings } =
		useDeleteErpIntegrationMapping();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteErpIntegrationMappings(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	const columnsWithAdditions = Columns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj,
	);

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">ERP Integration Mappings</h2>
			<hr />
			<div className="flex items-center justify-between mt-7 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchErpIntegrationMappings}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Code",
										fieldName: "code",
									},
									{
										title: "Mapping Code 1",
										fieldName: "mapping_code_1",
									},
									{
										title: "Mapping Code 2",
										fieldName: "mapping_code_2",
									},
									{
										title: "Sequence Number",
										fieldName: "sequenceNumber",
									},
								],
							},
							{
								title: "Payment Institution Branch",
								fieldType: "SELECT_FIELD",
								fieldName: "bankBranchId",
								options: paymentInstitutionBranches?.data || [],
							},
						]}
					/>
				</div>
				<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
					<div
						className="flex items-center text-primary-blue text-sm cursor-pointer w-fit"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-sm" />
						Add New Integration
					</div>
				</PermissionChecker>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.ERP_MAPPING_INTEGRATION}
				data={tableData}
				columns={mappingColumns}
				loading={isLoading}
				onRowClick={
					checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
						? (row, event) => {
								setFormData(row);
								setAnchorEl(event.currentTarget);
							}
						: undefined
				}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={mappingColumnVisibility}
				pageSize={10}
				hasPermission={checkUserPermissionsAnd([
					PERMISSIONS.COMPANY_CONFIGS_READ,
				])}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={
					checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
						? setAnchorEl
						: undefined
				}
				hasPermission={checkUserPermissionsAnd([
					PERMISSIONS.COMPANY_CONFIGS_READ,
				])}
			/> */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.code || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<ErpIntegrationMappingsModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Integration" : "Add New Integration"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					paymentInstitutionBranches={paymentInstitutionBranches?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
		</div>
	);
};

export default ErpIntegrationMappings;
